import FlowInitializer from '@/app/FlowInitializer';
import { FlowStatusBadges } from '@/app/guide/FlowStatusBadges';
import { useStartNewGuide } from '@/app/guide/useStartNewGuide';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { guideRowsState } from '@/state/guide.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { AppFlowParams } from '@rock/types';
import { ChevronRight } from 'lucide-react';
import type { FC } from 'react';

/**
 * Every guide a space or equipment type has, and where each one stands. The editor takes the whole
 * screen, so this is the only place the set of guides is visible at once — which one the app is
 * serving, which are drafts, and which has changes waiting to be published.
 *
 * It mounts inside a `FlowInitializer` for the new-guide dialog it raises, which lives there.
 */
export const GuidesTab: FC<{ params: AppFlowParams; editBase: string }> = ({ params, editBase }) => (
    <FlowInitializer params={params}>
        <GuidesTable params={params} editBase={editBase} />
    </FlowInitializer>
);

const GuidesTable: FC<{ params: AppFlowParams; editBase: string }> = ({ params, editBase }) => {
    const [rows, rowsLoadable] = useLoadableAtomValue(guideRowsState(params), []);
    const { canUseDefault, startEmpty, startFromDefault } = useStartNewGuide(params);
    const onRowClick = useRowClick();

    // Offering "Start New Guide" over guides that are still loading is how you end up with two.
    if (rowsLoadable.state !== 'hasData') {
        return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;
    }

    if (rows.length === 0) {
        return (
            <div className="space-y-3 rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                <div>No guides have been created yet.</div>
                <div className="flex flex-row justify-center gap-2">
                    <Button onClick={startEmpty}>Start New Guide (empty)</Button>
                    {canUseDefault && (
                        <Button variant="outline" onClick={startFromDefault}>
                            Start New Guide (from Default)
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end gap-2">
                {canUseDefault && (
                    <Button variant="outline" size="sm" className="cursor-pointer" onClick={startFromDefault}>
                        Add New (from Default)
                    </Button>
                )}
                <Button variant="outline" size="sm" className="cursor-pointer" onClick={startEmpty}>
                    Add New
                </Button>
            </div>
            <Table className="not-prose">
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Steps</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(({ flow, status }) => (
                        <TableRow
                            key={flow.id}
                            className="group/row hover:bg-muted cursor-pointer"
                            onClick={onRowClick(`${editBase}/${flow.id}`)}
                        >
                            <TableCell className="font-medium">{flow.name || flow.id}</TableCell>
                            <TableCell>
                                <FlowStatusBadges status={status} />
                            </TableCell>
                            <TableCell className="text-muted-foreground">{flow.nodes?.length ?? 0}</TableCell>
                            {/* The whole row opens the guide; the carat is just where that's headed. */}
                            <TableCell className="text-right">
                                <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
